import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { getProfile, getVehicles, getServiceRecords, getDocuments, updateClientProfile } from '../../lib/supabase'
import { format } from 'date-fns'
import {
  ArrowLeft, User, Car, Wrench, FileText, Mail, Phone, Calendar,
  Edit2, Save, X, CheckCircle, AlertCircle, Building2
} from 'lucide-react'

export default function AdminClientDetail() {
  const { clientId } = useParams()
  const [client, setClient] = useState(null)
  const [vehicles, setVehicles] = useState([])
  const [serviceRecords, setServiceRecords] = useState([])
  const [documents, setDocuments] = useState([])
  const [loading, setLoading] = useState(true)

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editSuccess, setEditSuccess] = useState(false)
  const [editError, setEditError] = useState('')
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    company_name: '',
    notes: ''
  })

  useEffect(() => {
    loadData()
  }, [clientId])

  useEffect(() => {
    if (client) {
      setFormData({
        full_name: client.full_name || '',
        phone: client.phone || '',
        company_name: client.company_name || '',
        notes: client.notes || ''
      })
    }
  }, [client])

  const loadData = async () => {
    try {
      const [profileRes, vehiclesRes, docsRes] = await Promise.all([
        getProfile(clientId),
        getVehicles(clientId),
        getDocuments(clientId)
      ])

      setClient(profileRes.data)
      setVehicles(vehiclesRes.data || [])
      setDocuments(docsRes.data || [])

      // Get service records for client's vehicles
      const vehicleIds = (vehiclesRes.data || []).map(v => v.id)
      const allServices = await getServiceRecords()
      const clientServices = (allServices.data || []).filter(s => vehicleIds.includes(s.vehicle_id))
      setServiceRecords(clientServices)
    } catch (err) {
      console.error('Error loading client:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleSaveProfile = async () => {
    setSaving(true)
    setEditError('')
    setEditSuccess(false)

    try {
      const { data, error } = await updateClientProfile(clientId, formData)
      if (error) throw error

      setClient(prev => ({ ...prev, ...formData }))
      setIsEditing(false)
      setEditSuccess(true)
      setTimeout(() => setEditSuccess(false), 3000)
    } catch (err) {
      console.error('Error updating client:', err)
      setEditError(err.message || 'Failed to update client profile')
    } finally {
      setSaving(false)
    }
  }

  const handleCancelEdit = () => {
    setFormData({
      full_name: client.full_name || '',
      phone: client.phone || '',
      company_name: client.company_name || '',
      notes: client.notes || ''
    })
    setIsEditing(false)
    setEditError('')
  }

  const totalServiceCost = serviceRecords.reduce((sum, r) => sum + (r.total_cost || 0), 0)

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-rdc-forest border-t-transparent" />
      </div>
    )
  }

  if (!client) {
    return (
      <div className="card p-12 text-center">
        <User size={48} className="text-rdc-warm-gray mx-auto mb-4" />
        <h3 className="font-display text-lg font-semibold text-black mb-2">Client not found</h3>
        <Link to="/admin/clients" className="btn-primary mt-4">Back to Clients</Link>
      </div>
    )
  }

  return (
    <div className="animate-fade-in">
      <Link to="/admin/clients" className="inline-flex items-center gap-2 text-rdc-taupe hover:text-rdc-primary mb-6">
        <ArrowLeft size={20} />
        Back to Clients
      </Link>

      {/* Client Profile Card — View/Edit */}
      <div className={`card p-6 mb-6 ${isEditing ? 'ring-2 ring-rdc-primary/30' : ''}`}>
        {editSuccess && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
            <CheckCircle size={18} />
            <span className="text-sm">Profile updated successfully</span>
          </div>
        )}

        {editError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
            <AlertCircle size={18} />
            <span className="text-sm">{editError}</span>
          </div>
        )}

        {isEditing ? (
          /* Edit Mode */
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="section-title">Edit Client Profile</h2>
              <button
                onClick={handleCancelEdit}
                disabled={saving}
                className="p-2 hover:bg-rdc-cream rounded-lg transition-colors text-rdc-taupe"
              >
                <X size={20} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-black mb-1">Full Name</label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                  className="input-field w-full"
                  placeholder="Enter full name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-black mb-1">Email</label>
                <input
                  type="email"
                  value={client.email}
                  disabled
                  className="input-field w-full bg-rdc-cream text-rdc-taupe cursor-not-allowed"
                />
                <p className="text-xs text-rdc-taupe mt-1">Email is managed by authentication</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-black mb-1">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="input-field w-full"
                  placeholder="(555) 123-4567"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-black mb-1">Company / Organization</label>
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                  className="input-field w-full"
                  placeholder="Company name"
                />
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-black mb-1">Admin Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="input-field w-full resize-none"
                rows="3"
                placeholder="Internal notes about this client (only visible to admins)"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleSaveProfile}
                disabled={saving}
                className="btn-primary flex items-center gap-2"
              >
                <Save size={18} />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={handleCancelEdit}
                disabled={saving}
                className="px-4 py-2 border border-rdc-warm-gray text-rdc-dark-gray rounded-lg hover:bg-rdc-cream transition-colors"
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          /* View Mode */
          <>
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-full bg-rdc-primary/10 flex items-center justify-center font-bold text-2xl text-rdc-primary">
                {client.full_name?.charAt(0) || '?'}
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <h1 className="page-title">{client.full_name || 'Unnamed Client'}</h1>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-rdc-primary hover:bg-rdc-cream rounded-lg transition-colors"
                  >
                    <Edit2 size={16} />
                    Edit
                  </button>
                </div>
                <div className="flex flex-wrap gap-4 mt-2 text-sm text-rdc-taupe">
                  <span className="flex items-center gap-1">
                    <Mail size={16} />
                    {client.email}
                  </span>
                  {client.phone && (
                    <span className="flex items-center gap-1">
                      <Phone size={16} />
                      {client.phone}
                    </span>
                  )}
                  {client.company_name && (
                    <span className="flex items-center gap-1">
                      <Building2 size={16} />
                      {client.company_name}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Calendar size={16} />
                    Member since {format(new Date(client.member_since || client.created_at), 'MMMM yyyy')}
                  </span>
                </div>
              </div>
            </div>

            {/* Admin Notes */}
            {client.notes && (
              <div className="mt-4 pt-4 border-t border-rdc-warm-gray/30">
                <div className="text-xs font-semibold text-rdc-taupe uppercase tracking-wide mb-2">Admin Notes</div>
                <p className="text-sm text-rdc-dark-gray bg-rdc-cream/50 p-3 rounded-lg">{client.notes}</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="card p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-rdc-primary/10 flex items-center justify-center">
              <Car size={20} className="text-rdc-primary" />
            </div>
            <div>
              <div className="text-2xl font-bold text-black">{vehicles.length}</div>
              <div className="text-sm text-rdc-taupe">Vehicles</div>
            </div>
          </div>
        </div>
        <div className="card p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-rdc-olive/10 flex items-center justify-center">
              <Wrench size={20} className="text-rdc-olive" />
            </div>
            <div>
              <div className="text-2xl font-bold text-black">{serviceRecords.length}</div>
              <div className="text-sm text-rdc-taupe">Service Records</div>
            </div>
          </div>
        </div>
        <div className="card p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-rdc-forest/10 flex items-center justify-center">
              <FileText size={20} className="text-rdc-forest" />
            </div>
            <div>
              <div className="text-2xl font-bold text-rdc-forest">${totalServiceCost.toLocaleString()}</div>
              <div className="text-sm text-rdc-taupe">Total Service Costs</div>
            </div>
          </div>
        </div>
      </div>

      {/* Vehicles */}
      <div className="card p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="section-title">Vehicles</h2>
          <Link to={`/admin/vehicles/new?client=${clientId}`} className="btn-primary text-sm py-2">
            Add Vehicle
          </Link>
        </div>

        {vehicles.length === 0 ? (
          <div className="text-center py-8 text-rdc-taupe">
            <Car size={32} className="mx-auto mb-2 text-rdc-warm-gray" />
            <p>No vehicles yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {vehicles.map(vehicle => (
              <Link
                key={vehicle.id}
                to={`/admin/vehicles/${vehicle.id}/edit`}
                className="flex items-center gap-4 p-4 bg-rdc-cream rounded-lg hover:bg-rdc-warm-gray/30 transition-colors"
              >
                <div className="w-16 h-16 rounded-lg bg-white overflow-hidden flex-shrink-0">
                  {vehicle.primary_image_url ? (
                    <img src={vehicle.primary_image_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Car size={24} className="text-rdc-warm-gray" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-black">{vehicle.year} {vehicle.make} {vehicle.model}</div>
                  <div className="text-sm text-rdc-taupe">{vehicle.color}</div>
                  <div className="text-sm text-rdc-taupe">{vehicle.mileage?.toLocaleString()} mi</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Recent Service */}
      <div className="card p-6">
        <h2 className="section-title mb-4">Recent Service History</h2>

        {serviceRecords.length === 0 ? (
          <div className="text-center py-8 text-rdc-taupe">
            <Wrench size={32} className="mx-auto mb-2 text-rdc-warm-gray" />
            <p>No service records yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {serviceRecords.slice(0, 5).map(record => (
              <div key={record.id} className="flex items-center justify-between p-3 bg-rdc-cream rounded-lg">
                <div>
                  <div className="font-medium text-black">{record.service_type}</div>
                  <div className="text-sm text-rdc-taupe">
                    {record.vehicle?.year} {record.vehicle?.make} {record.vehicle?.model}
                    {' • '}{format(new Date(record.service_date), 'MMM d, yyyy')}
                  </div>
                </div>
                <div className="font-semibold text-rdc-forest">${record.total_cost?.toLocaleString()}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
